import {openDB, deleteDB, type IDBPDatabase} from 'idb';

/** IndexedDB store name for the mutation queue */
const STORE_NAME = 'mutation_queue';

export interface MutationRecord {
  /** Unique identifier (crypto.randomUUID) */
  id: string;
  /** HTTP method: POST, PUT, PATCH, DELETE */
  method: string;
  /** Request URL (relative to baseURL) */
  url: string;
  /** Request headers */
  headers: Record<string, string>;
  /** Serialized request body (JSON string) */
  body: string;
  /** ISO timestamp when the mutation was queued */
  createdAt: string;
  /** Number of replay attempts */
  retryCount: number;
  /** Current status */
  status: 'pending' | 'replaying' | 'failed';
}

export interface MutationQueue {
  /** Add a mutation to the queue. Returns the generated id. */
  enqueue: (
    mutation: Omit<MutationRecord, 'id' | 'createdAt' | 'retryCount' | 'status'>,
  ) => Promise<string>;
  /** Remove a mutation from the queue by id. */
  dequeue: (id: string) => Promise<void>;
  /** Return the oldest pending mutation without removing it. */
  peek: () => Promise<MutationRecord | undefined>;
  /** Mark a mutation as failed and increment its retryCount. */
  markFailed: (id: string) => Promise<void>;
  /** Return all mutations in the queue, sorted by createdAt ascending. */
  getAll: () => Promise<MutationRecord[]>;
  /** Return the total number of mutations in the queue. */
  getCount: () => Promise<number>;
  /** Remove all mutations from the queue. */
  clear: () => Promise<void>;
  /** Delete the entire database (for logout / PDPA compliance). */
  deleteDatabase: () => Promise<void>;
  /** Remove mutations older than maxAgeMs milliseconds. */
  purgeStale: (maxAgeMs: number) => Promise<number>;
}

/**
 * Creates a mutation queue backed by IndexedDB.
 *
 * Uses a per-user database (`dyna-queue-{userId}`) so logout can
 * cleanly delete the entire database (PDPA compliance).
 */
export function createMutationQueue(userId: string): MutationQueue {
  const dbName = `dyna-queue-${userId}`;
  let dbPromise: Promise<IDBPDatabase> | null = null;

  function getDB(): Promise<IDBPDatabase> {
    if (!dbPromise) {
      dbPromise = openDB(dbName, 1, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME);
          }
        },
      });
    }
    return dbPromise;
  }

  return {
    async enqueue(mutation): Promise<string> {
      const db = await getDB();
      const id = crypto.randomUUID();
      const record: MutationRecord = {
        ...mutation,
        id,
        createdAt: new Date().toISOString(),
        retryCount: 0,
        status: 'pending',
      };
      await db.put(STORE_NAME, record, id);
      return id;
    },

    async dequeue(id): Promise<void> {
      const db = await getDB();
      await db.delete(STORE_NAME, id);
    },

    async peek(): Promise<MutationRecord | undefined> {
      const db = await getDB();
      const all = (await db.getAll(STORE_NAME)) as MutationRecord[];
      const sorted = all.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      return sorted.find((m) => m.status === 'pending');
    },

    async markFailed(id): Promise<void> {
      const db = await getDB();
      const record = (await db.get(STORE_NAME, id)) as MutationRecord | undefined;
      if (!record) return;

      record.status = 'failed';
      record.retryCount += 1;
      await db.put(STORE_NAME, record, id);
    },

    async getAll(): Promise<MutationRecord[]> {
      const db = await getDB();
      const all = (await db.getAll(STORE_NAME)) as MutationRecord[];
      // Sort by createdAt ascending (FIFO)
      return all.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    },

    async getCount(): Promise<number> {
      const db = await getDB();
      return db.count(STORE_NAME);
    },

    async clear(): Promise<void> {
      const db = await getDB();
      await db.clear(STORE_NAME);
    },

    async deleteDatabase(): Promise<void> {
      if (dbPromise) {
        const db = await dbPromise;
        db.close();
        dbPromise = null;
      }
      await deleteDB(dbName);
    },

    async purgeStale(maxAgeMs: number): Promise<number> {
      const db = await getDB();
      const all = (await db.getAll(STORE_NAME)) as MutationRecord[];
      const now = Date.now();
      let purged = 0;

      for (const record of all) {
        if (now - new Date(record.createdAt).getTime() > maxAgeMs) {
          await db.delete(STORE_NAME, record.id);
          purged++;
        }
      }

      return purged;
    },
  };
}
